import { AUTH0_REALM, AUTH0_SCOPE, auth0ConfigForEnv, auth0TenantForEnv } from "./env_config";

const GRANT_TYPE = "http://auth0.com/oauth/grant-type/password-realm";

export const TOKEN_KEY = "authToken";
export const TOKEN_EXPIRES_AT_KEY = "authTokenExpiresAt";
// Holds the Auth0 tenant the token was minted for ("devQa" / "prodUat"), not the app
// environment: dev and qa share a token, and so do the four uat/prod environments.
export const TOKEN_ENV_KEY = "authTokenEnv";

// Treat a token as expired slightly early so a request cannot land on the far side of
// the real expiry.
const EXPIRY_SKEW_MS = 60 * 1000;

// Held in memory only, deliberately never persisted. They exist so that switching to an
// environment on the other Auth0 tenant can re-authenticate silently. A page reload
// clears them, which is why a reload followed by a cross-tenant switch forces a re-login.
let credentials = null;

// The re-authentication currently in progress, if any, so parallel callers can share it.
let inFlight = null;

export const setCredentials = (email, password) => {
  credentials = { email, password };
};

export const getCredentials = () => credentials;

export const clearCredentials = () => {
  credentials = null;
};

export class Auth0Error extends Error {
  constructor(message, code) {
    super(message);
    this.name = "Auth0Error";
    this.code = code;
  }
}

// INVALID_CREDENTIALS is exported so the login page can use the exact same string when it
// rejects an unknown username before ever making a request. If the two differed even by
// punctuation, comparing them would tell an attacker which accounts exist.
export const INVALID_CREDENTIALS = "Invalid username or password";
const LOGIN_UNAVAILABLE = "Login is unavailable right now. Please contact an administrator.";
const LOGIN_UNREACHABLE = "Could not reach the login service. Check your connection and try again.";

// How each Auth0 error code is explained to a developer. Never shown to a user, and
// referenced only from DIAGNOSTICS_ENABLED branches so it drops out of a release build.
const DIAGNOSES = {
  unauthorized_client: "the Password grant is not enabled on this Auth0 client",
  mfa_required: "the account enforces MFA, which the password grant cannot satisfy",
  access_denied: "Auth0 denied the request; the client may be confidential and expecting a secret",
  invalid_request: "the token request was malformed"
};

const DIAGNOSTICS_ENABLED = process.env.NODE_ENV !== "production";

/**
 * Exchange an email and password for an Auth0 access token via the Resource Owner
 * Password Grant. Never logs the password or the token.
 */
export const requestToken = async ({ email, password, env }) => {
  const config = auth0ConfigForEnv(env);

  if (!config || !config.clientId) {
    if (DIAGNOSTICS_ENABLED) {
      console.error(
        config
          ? `No Auth0 client id is configured for the "${env}" environment.`
          : `No Auth0 tenant is configured for the "${env}" environment.`
      );
    }

    throw new Auth0Error(LOGIN_UNAVAILABLE, "not_configured");
  }

  let response;
  try {
    response = await fetch(`https://${config.domain}/oauth/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        grant_type: GRANT_TYPE,
        realm: AUTH0_REALM,
        client_id: config.clientId,
        audience: config.audience,
        scope: AUTH0_SCOPE,
        username: email,
        password
      })
    });
  } catch (err) {
    if (DIAGNOSTICS_ENABLED) {
      console.error(
        `Auth0 token request to ${config.domain} could not be completed: ${err?.message}. ` +
          "If the network tab shows the request completing, this is CORS: add this origin to " +
          "the Auth0 application's Allowed Web Origins."
      );
    }

    throw new Auth0Error(LOGIN_UNREACHABLE, "network_error");
  }

  const body = await response.json().catch(() => ({}));

  if (!response.ok || !body.access_token) {
    const code = body?.error;

    if (code === "invalid_grant") {
      throw new Auth0Error(INVALID_CREDENTIALS, code);
    }

    if (DIAGNOSTICS_ENABLED) {
      console.error(
        `Auth0 token request to ${config.domain} failed [${code ?? "unknown error"}]: ` +
          (DIAGNOSES[code] ?? body?.error_description ?? "no error_description returned")
      );
    }

    throw new Auth0Error(LOGIN_UNAVAILABLE, code);
  }

  return {
    token: body.access_token,
    expiresAt: Date.now() + (body.expires_in ?? 0) * 1000
  };
};

export const storeToken = ({ token, expiresAt, env }) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(expiresAt));
  localStorage.setItem(TOKEN_ENV_KEY, auth0TenantForEnv(env));
};

export const clearStoredToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
  localStorage.removeItem(TOKEN_ENV_KEY);
};

const storedTokenFor = (env) => {
  const tenant = auth0TenantForEnv(env);
  const token = localStorage.getItem(TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRES_AT_KEY));

  if (!tenant || !token || localStorage.getItem(TOKEN_ENV_KEY) !== tenant) {
    return null;
  }

  return expiresAt - EXPIRY_SKEW_MS > Date.now() ? token : null;
};

/**
 * Authenticate against `env`'s Auth0 tenant, remembering the credentials in memory and
 * persisting the token. Throws an Auth0Error with a displayable message on failure.
 */
export const authenticate = async ({ email, password, env }) => {
  const { token, expiresAt } = await requestToken({ email, password, env });

  setCredentials(email, password);
  storeToken({ token, expiresAt, env });

  return token;
};

/**
 * The token to use for `env`: the stored one when it was minted by the same Auth0 tenant
 * and has not expired, otherwise a fresh one from the in-memory credentials. Returns null
 * when neither is available, which means the user has to sign in again.
 */
export const getValidToken = async (env) => {
  const stored = storedTokenFor(env);
  if (stored) {
    return stored;
  }

  const tenant = auth0TenantForEnv(env);
  if (!tenant || !credentials) {
    return null;
  }

  if (!inFlight || inFlight.tenant !== tenant) {
    inFlight = {
      tenant,
      promise: authenticate({ ...credentials, env })
        .catch(() => null)
        .finally(() => {
          inFlight = null;
        })
    };
  }

  return inFlight.promise;
};

export const clearAuthSession = () => {
  clearCredentials();
  clearStoredToken();
  inFlight = null;
};
